import React, { useRef, useState } from 'react'
import classNames from 'classnames'
import classes from './PurchaseHistoryTable.module.css'
import { convertToFormatted, formatDate } from '../../util/functions'
import PurchaseHistoryLoader from '../loaders/PurchaseHistoryLoader'
import { ReactComponent as DownIcon } from '../../assets/icons/down.svg'
import { ReactComponent as UpIcon } from '../../assets/icons/up.svg'

const Cell = ({
    className,
    children,
    header = false,
    small = false,
    ...props
}) => (
    <span
        className={classNames(
            classes.purchaseHistoryCell,
            header && classes.purchaseHistoryCellHeader,
            small && classes.smallCell,
            className
        )}
        role={header ? 'columnheader' : 'cell'}
        {...props}
    >
        {children}
    </span>
)

const ChildCell = ({
    className,
    children,
    header = false,
    double = false,
    center = false,
    ...props
}) => (
    <span
        className={classNames(
            classes.purchaseHistoryChildCell,
            header && classes.purchaseHistoryChildCellHeader,
            double && classes.purchaseHistoryChildCellDouble,
            center && classes.purchaseHistoryChildCellCenter,
            className
        )}
        role={header ? 'columnheader' : 'cell'}
        {...props}
    >
        {children}
    </span>
)

const items = [
    {
        name: 'Business management',
        quantity: 1,
        price: 2500,
    },
    {
        name: 'Boss management',
        quantity: 5,
        price: 500,
    },
]

const PurchaseHistoryItem = ({
    reference,
    date,
    price,
    amountPaid,
    loyaltyPoints,
    items,
}) => {
    const [itemsActive, setItemsActive] = useState(false)

    const itemsRef = useRef()

    return (
        <div className={classes.purchaseHistoryItemWrapper} role="rowgroup">
            <div
                className={classes.purchaseHistoryItem}
                role="row"
                aria-expanded={itemsActive}
                aria-controls={`items-${reference}`}
                onClick={_ => setItemsActive(!itemsActive)}
            >
                <Cell>
                    <p className={classes.itemTitle}>{reference}</p>
                </Cell>
                <Cell>{formatDate(date)}</Cell>
                <Cell className={classes.boldCell}>
                    {convertToFormatted(price, true)}
                </Cell>
                <Cell>{convertToFormatted(amountPaid, true)}</Cell>
                <Cell>
                    {convertToFormatted(loyaltyPoints, false, true)} LPs
                </Cell>
                <Cell small>
                    <div
                        className={classes.expandButton}
                        role="button"
                        onClick={e => {
                            e.stopPropagation()
                            setItemsActive(!itemsActive)
                        }}
                        aria-label="View order details"
                    >
                        {itemsActive ? <UpIcon /> : <DownIcon />}
                    </div>
                </Cell>
            </div>
            <div
                className={classNames(
                    classes.purchaseHistoryItemChildren,
                    itemsActive && classes.purchaseHistoryItemChildrenActive
                )}
                ref={itemsRef}
                style={{
                    height: `${
                        itemsActive ? itemsRef.current?.scrollHeight || 0 : 0
                    }px`,
                }}
                id={`items-${reference}`}
                role="table"
                aria-hidden={!itemsActive}
            >
                <div
                    className={classNames(
                        classes.purchaseHistoryItemChildrenRow,
                        classes.purchaseHistoryItemChildrenHeaderRow
                    )}
                    role="row"
                >
                    <ChildCell header double>
                        Description
                    </ChildCell>
                    <ChildCell header center>
                        Quantity
                    </ChildCell>
                    <ChildCell header>Amount</ChildCell>
                </div>
                <span className={classes.splitLine} role="presentation" />
                {items.map(({ description, quantity, amount }) => (
                    <div
                        className={classes.purchaseHistoryItemChildrenRow}
                        role="row"
                    >
                        <ChildCell double>{description}</ChildCell>
                        <ChildCell center>{quantity}</ChildCell>
                        <ChildCell>
                            {convertToFormatted(amount, true, true)}
                        </ChildCell>
                    </div>
                ))}
                <span className={classes.splitLine} role="presentation" />
                <div className={classes.purchaseHistoryItemChildrenBottom}>
                    <span
                        className={
                            classes.purchaseHistoryItemChildrenBottomText
                        }
                    >
                        Total Amount:{' '}
                        {convertToFormatted(amountPaid, true, true)}
                    </span>
                </div>
            </div>
        </div>
    )
}

export const PurchaseHistoryTable = ({
    items,
    className,
    loading,
    ...props
}) => (
    <div className={classes.purchaseHistoryTableContainer}>
        <div
            className={classNames(classes.purchaseHistoryTable, className)}
            role="table"
            {...props}
        >
            <div className={classes.tableHeader} role="rowgroup">
                <div className={classes.purchaseHistoryHeader} role="row">
                    <Cell header>Order ID</Cell>
                    <Cell header>Date</Cell>
                    <Cell header>Amount</Cell>
                    <Cell header>Amt. Paid</Cell>
                    <Cell header>Loyalty Points</Cell>
                    <Cell header small aria-label="Action"></Cell>
                </div>
            </div>
            <div className={classes.tableContent} role="rowgroup">
                {loading ? (
                    <>
                        <PurchaseHistoryLoader />
                        <PurchaseHistoryLoader />
                        <PurchaseHistoryLoader />
                    </>
                ) : (
                    items.map(item => <PurchaseHistoryItem {...item} />)
                )}
                {!loading && items.length === 0 && (
                    <h1 className={classes.emptyLabel}>No Payments Made</h1>
                )}
            </div>
        </div>
    </div>
)
